/**
 * ENG-5688 / ENG-5967 — on-disk state for `agt impersonate`.
 *
 * Layout under `~/.augmented-impersonate/`:
 *
 *   active/session-<hash>.json — one manifest PER impersonation session,
 *                                  keyed by a hash of its project_cwd. This
 *                                  is what lets multiple agents be
 *                                  impersonated concurrently from separate
 *                                  terminals (each `connect` runs in its own
 *                                  dedicated directory — process.cwd() or an
 *                                  auto-provisioned `--workdir`).
 *   active/manifest.json       — LEGACY singleton from the pre-ENG-5967
 *                                  one-at-a-time design. Migrated into the
 *                                  keyed layout on first access (see
 *                                  `migrateLegacyManifest`) so an in-flight
 *                                  session created by an older CLI survives
 *                                  the upgrade.
 *   <code-name>/CLAUDE.md      — persona files rendered for THIS agent;
 *   <code-name>/.mcp.json        kept side-by-side so the operator can diff
 *                                two agents' configs cheaply.
 *
 * Each manifest records what files we backed up in its project directory so
 * `exit` can restore them precisely. Keying by project_cwd means two
 * sessions never share backup state, and exiting one never disturbs another.
 *
 * Concurrency: every write is atomic (tmp + rename) and each session is its
 * own file, so concurrent `connect`s in different directories never race on
 * shared state.
 */

import { createHash } from 'node:crypto';
import {
  chmodSync,
  existsSync,
  mkdirSync,
  readdirSync,
  readFileSync,
  renameSync,
  rmSync,
  writeFileSync,
} from 'node:fs';
import { homedir } from 'node:os';
import { join } from 'node:path';

const IMPERSONATE_ROOT = join(homedir(), '.augmented-impersonate');
const ACTIVE_DIR = join(IMPERSONATE_ROOT, 'active');
/**
 * Legacy singleton path from the pre-ENG-5967 one-session-at-a-time design.
 * Retained only so `migrateLegacyManifest` can fold a pre-upgrade session
 * into the per-cwd layout. New writes never target it.
 */
const ACTIVE_MANIFEST_PATH = join(ACTIVE_DIR, 'manifest.json');
const SESSION_FILE_PREFIX = 'session-';
const SESSION_FILE_SUFFIX = '.json';

/**
 * Per-session manifest path, keyed by a hash of the project directory. The
 * hash keeps the filename filesystem-safe and fixed-length regardless of how
 * long or exotic the project path is; collisions are not a concern at
 * SHA-256/16-hex-chars for the handful of dirs one operator impersonates in.
 */
function sessionManifestPath(projectCwd: string): string {
  const hash = createHash('sha256').update(projectCwd).digest('hex').slice(0, 16);
  return join(ACTIVE_DIR, `${SESSION_FILE_PREFIX}${hash}${SESSION_FILE_SUFFIX}`);
}

/**
 * Records what `select` found at a given path in the operator's project
 * before swapping it for the impersonation persona, so `exit` can put
 * things back exactly the way they were.
 *
 *  - "had-file": a regular file existed and was renamed to
 *                <path>.pre-impersonate-backup. Restore = rename back.
 *  - "was-symlink": a symlink existed (e.g. a stale prior impersonation)
 *                and was simply removed. Restore = nothing (the operator
 *                presumably re-runs select if they wanted it back).
 *  - "didnt-exist": nothing was there; we just made a symlink. Restore =
 *                remove the symlink.
 */
export type BackupKind = 'had-file' | 'was-symlink' | 'didnt-exist';

/**
 * ENG-5750 — records what the introduce SessionStart hook touched in the
 * operator's project dir so `exit` can leave it exactly as it found it.
 *
 *  - `settings_path`: the `.claude/settings.local.json` we upserted the
 *    SessionStart hook into.
 *  - `settings_backup`: "had-file" → the original was copied to
 *    `<path>.pre-impersonate-backup` and must be restored on exit;
 *    "didnt-exist" → we created it and must remove it on exit.
 *  - `created_claude_dir`: we created the `.claude` directory and should
 *    remove it on exit if it's empty afterwards.
 */
export interface HookBackup {
  settings_path: string;
  settings_backup: 'had-file' | 'didnt-exist';
  created_claude_dir: boolean;
}

/**
 * ENG-5801 — records what the impersonation statusLine upsert touched in
 * the operator's project dir so `exit` can leave it exactly as it found
 * it. Same shape as HookBackup because both features share the same
 * `.claude/settings.local.json` byte-for-byte backup mechanism (whichever
 * register runs first creates `<path>.pre-impersonate-backup`; whichever
 * unregister runs first restores it — the existence guards in
 * register/unregister make the ordering irrelevant).
 *
 *  - `settings_path`: the `.claude/settings.local.json` we upserted the
 *    `statusLine` key into.
 *  - `settings_backup`: "had-file" → original was copied to
 *    `<path>.pre-impersonate-backup` and must be restored on exit;
 *    "didnt-exist" → we created it and must remove it on exit.
 *  - `created_claude_dir`: we created the `.claude` directory and should
 *    remove it on exit if it's empty afterwards.
 */
export interface StatusLineBackup {
  settings_path: string;
  settings_backup: 'had-file' | 'didnt-exist';
  created_claude_dir: boolean;
}

/**
 * Singleton manifest. Reused for both writing during `connect` and
 * reading during `current` / `exit`. The shape is meant to be append-only
 * — fields added later should be optional so older manifests still load
 * after an upgrade.
 */
export interface ImpersonateManifest {
  /** Agent code_name (kebab-case, e.g. `data-jane`). */
  code_name: string;
  /** Agent UUID, mirror of `act_as_agent_id` claim on the JWT. */
  agent_id: string;
  /** Team UUID the agent belongs to. */
  team_id: string;
  /** Server-issued impersonation JWT — keep opaque to the CLI. */
  token: string;
  /** Unix-seconds (matches the API's response.expires_at). */
  expires_at: number;
  /** Server-issued correlator (claim `sid`). */
  session_id: string;
  /** ISO-8601 timestamp the manifest was written (for `current`). */
  minted_at: string;
  /** process.cwd() at the time of `connect`. */
  project_cwd: string;
  /** What we found in project_cwd before swapping (see BackupKind). */
  backups: Record<string, BackupKind>;
  /**
   * Augmented API host (no trailing slash) the redeem token was
   * exchanged against. `exit` calls /impersonate/agent/stop against the
   * same host so the audit row lands on the right environment — operators
   * who flip AGT_HOST between dev/prod between connect and exit otherwise
   * miss the stop server-side. ENG-5688 (redesigned flow).
   */
  host: string;
  /**
   * ENG-5750 — the introduce SessionStart hook registered in
   * project_cwd, if any. Optional so older manifests still load. `exit`
   * uses it to remove/restore the hook and leave the cwd clean.
   */
  hook?: HookBackup;
  /**
   * ENG-5801 — the impersonation statusLine registered in project_cwd,
   * if any. Optional so older manifests still load (same precedent as
   * `hook?`). `exit` uses it to remove/restore the statusLine and leave
   * the cwd clean.
   */
  statusLine?: StatusLineBackup;
}

/**
 * Per-code-name directory holding the rendered persona files. Created
 * on demand by `ensureCodeNameDir()`.
 */

/**
 * CR #3322333801: codeName flows directly into `join(IMPERSONATE_ROOT,
 * codeName)`. A value like `../...` would let a hostile redeem response
 * write outside ~/.augmented-impersonate/. We can't fully trust the API
 * server's response (defensive depth), and code_name is supposed to be
 * kebab-case anyway per the codebase's naming conventions — validate
 * up-front and throw if it's not.
 */
const CODE_NAME_RE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

function assertValidCodeName(codeName: string): void {
  if (!CODE_NAME_RE.test(codeName)) {
    throw new Error(
      `Invalid agent code_name "${codeName}" — must be lowercase kebab-case (a-z, 0-9, hyphens; no leading/trailing/consecutive hyphens).`,
    );
  }
}

export function getImpersonateCodeNameDir(codeName: string): string {
  assertValidCodeName(codeName);
  return join(IMPERSONATE_ROOT, codeName);
}

export function ensureCodeNameDir(codeName: string): string {
  // assertValidCodeName runs inside getImpersonateCodeNameDir, but we
  // call it again explicitly so mkdirSync gets a validated input even
  // if a future change unwires the helper.
  assertValidCodeName(codeName);
  const dir = getImpersonateCodeNameDir(codeName);
  mkdirSync(dir, { recursive: true });
  return dir;
}

/**
 * ENG-5756 (AC3) — dedicated, agent-scoped working directory used by
 * `agt impersonate connect --workdir`. Lives under the per-code-name
 * persona dir (so it's clearly owned by impersonation and sits beside
 * the persona files the swap links to). Persona files get symlinked in
 * HERE instead of into the operator's cwd, which makes clobbering a repo
 * working tree structurally impossible rather than merely recoverable.
 */
export function ensureImpersonateWorkdir(codeName: string): string {
  const dir = join(ensureCodeNameDir(codeName), 'workdir');
  mkdirSync(dir, { recursive: true });
  return dir;
}

/**
 * Strict structural validation, factored out so both the single-session
 * read and the multi-session `listActiveManifests` enforce the exact same
 * contract. Returns the validated manifest or null.
 *
 * CR #3321347807: validate EVERY required field, including `backups`. A
 * half-written manifest that passed the old four-field check would later
 * crash `exit` on `Object.entries(manifest.backups)` when backups was
 * undefined. Bail to null rather than throw so callers treat corrupted
 * state as "nothing to restore" rather than entering an error loop.
 */
function validateManifest(parsed: unknown): ImpersonateManifest | null {
  if (typeof parsed !== 'object' || parsed === null) return null;
  const m = parsed as Record<string, unknown>;

  const backupsOk =
    typeof m.backups === 'object' &&
    m.backups !== null &&
    Object.values(m.backups as Record<string, unknown>).every(
      (v) => v === 'had-file' || v === 'was-symlink' || v === 'didnt-exist',
    );

  if (
    typeof m.code_name !== 'string' ||
    typeof m.agent_id !== 'string' ||
    typeof m.team_id !== 'string' ||
    typeof m.token !== 'string' ||
    typeof m.expires_at !== 'number' ||
    typeof m.session_id !== 'string' ||
    typeof m.minted_at !== 'string' ||
    typeof m.project_cwd !== 'string' ||
    // ENG-5688 (redesigned): host required so `exit` calls /stop against
    // the right environment. Required, not optional, because writing the
    // wrong host on an existing session would point exit at a server
    // that has no record of the session — silently misleading.
    typeof m.host !== 'string' ||
    m.host.length === 0 ||
    !backupsOk
  ) {
    return null;
  }
  return parsed as ImpersonateManifest;
}

/** Read + validate a single manifest file, or null on any problem. */
function readManifestFile(path: string): ImpersonateManifest | null {
  if (!existsSync(path)) return null;
  try {
    return validateManifest(JSON.parse(readFileSync(path, 'utf-8')));
  } catch {
    return null;
  }
}

/**
 * ENG-5967 — fold a pre-upgrade legacy singleton (`active/manifest.json`)
 * into the per-cwd layout. Idempotent and best-effort: a valid legacy
 * manifest is moved to its keyed path (unless one already exists there,
 * in which case the keyed file wins and the legacy is discarded); an
 * unreadable/invalid legacy file is removed so it can't shadow future
 * reads. Called at the start of every public accessor so an older CLI's
 * in-flight session keeps working after the operator upgrades.
 */
function migrateLegacyManifest(): void {
  if (!existsSync(ACTIVE_MANIFEST_PATH)) return;
  const legacy = readManifestFile(ACTIVE_MANIFEST_PATH);
  try {
    if (!legacy) {
      rmSync(ACTIVE_MANIFEST_PATH, { force: true });
      return;
    }
    const target = sessionManifestPath(legacy.project_cwd);
    if (!existsSync(target)) {
      mkdirSync(ACTIVE_DIR, { recursive: true, mode: 0o700 });
      renameSync(ACTIVE_MANIFEST_PATH, target);
      chmodSync(target, 0o600);
    } else {
      // A keyed session for this dir already exists — it's authoritative.
      rmSync(ACTIVE_MANIFEST_PATH, { force: true });
    }
  } catch {
    // Best-effort: never let a migration hiccup break the caller.
  }
}

/**
 * Reads the active manifest for `projectCwd` if present. Returns null when
 * no impersonation is active in that directory — `current` and `exit` use
 * this to decide whether there's anything to act on.
 *
 * Does NOT check expiry — that's the caller's call. `current` reports the
 * expiry; `exit` proceeds with cleanup either way (a manifest pointing at
 * an expired JWT is still authoritative for the local backup state).
 */
export function readActiveManifest(projectCwd: string): ImpersonateManifest | null {
  migrateLegacyManifest();
  return readManifestFile(sessionManifestPath(projectCwd));
}

/**
 * ENG-5967 — every active impersonation session on this machine. Used by
 * `current --all` / `exit --all` and by the lazy auto-exit sweep so a
 * single command can reason about (or clean up) all sessions at once.
 * Invalid/half-written files are skipped, not thrown on.
 */
export function listActiveManifests(): ImpersonateManifest[] {
  migrateLegacyManifest();
  let entries: string[];
  try {
    entries = readdirSync(ACTIVE_DIR);
  } catch {
    return []; // active dir doesn't exist yet
  }
  const out: ImpersonateManifest[] = [];
  for (const entry of entries) {
    if (!entry.startsWith(SESSION_FILE_PREFIX) || !entry.endsWith(SESSION_FILE_SUFFIX)) {
      continue;
    }
    const manifest = readManifestFile(join(ACTIVE_DIR, entry));
    if (manifest) out.push(manifest);
  }
  return out;
}

/**
 * Writes the active manifest. Creates the directory if needed and uses
 * `renameSync` to swap atomically so a half-written file doesn't end up
 * on disk if the process is killed mid-write.
 *
 * CR #3322333807: the manifest contains the impersonation token (a
 * bearer credential), so the file and its parent directory are written
 * with restrictive permissions: 0700 on the directory, 0600 on the
 * file (owner read/write only — group/world get nothing). The explicit
 * chmodSync after writeFileSync is belt-and-braces: writeFileSync's
 * `mode` is the create-mode but doesn't take effect if the path already
 * exists. chmodSync forces it down to 0600 either way.
 */
export function writeActiveManifest(manifest: ImpersonateManifest): void {
  migrateLegacyManifest();
  mkdirSync(ACTIVE_DIR, { recursive: true, mode: 0o700 });
  const path = sessionManifestPath(manifest.project_cwd);
  const tmp = path + '.tmp';
  writeFileSync(tmp, JSON.stringify(manifest, null, 2), { mode: 0o600 });
  chmodSync(tmp, 0o600);
  renameSync(tmp, path);
}

/**
 * Removes the active manifest for `projectCwd`. Idempotent — calling it
 * twice, or when no manifest exists for that directory, is a no-op. Other
 * directories' sessions are untouched.
 */
export function clearActiveManifest(projectCwd: string): void {
  migrateLegacyManifest();
  const path = sessionManifestPath(projectCwd);
  if (existsSync(path)) {
    rmSync(path);
  }
}

/**
 * True if the manifest's expires_at has passed. Used by `current` to
 * tell the operator "your session has expired, you should re-select or
 * exit".
 */
export function isExpired(manifest: ImpersonateManifest, now = Date.now()): boolean {
  return manifest.expires_at * 1000 <= now;
}

// Exported for tests; intentionally not part of the documented API.
export const TEST_ONLY = {
  IMPERSONATE_ROOT,
  ACTIVE_DIR,
  /** Legacy singleton path — retained for migration tests. */
  ACTIVE_MANIFEST_PATH,
  /** Resolve the per-cwd keyed manifest path (ENG-5967). */
  sessionManifestPath,
};
